import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { sidebarConfig as pharmaSidebarConfig } from '@json/projects/pharma-app/src/config-farma';
import { sidebarConfig as quantumSidebarConfig } from '@json/projects/quantum-app/src/config-quantum';
import { sidebarConfig as hospitalPharmaSidebarConfig } from '@json/projects/hospital-pharma-app/src/config-farma';

import { sidebarConfigItem } from '@interfaces/sidebar.interface';
import { RoleManagerService } from '@services/role-manager.service';
import { CallsService } from '@services/api/calls.service';
import { LoggerService } from '@services/logger.service';
import { UserAction } from '@enums/user-action';

@Component({
    selector: 'app-report-select',
    templateUrl: './report-select.component.html',
    styleUrls: ['./report-select.component.scss'],
})
export class ReportSelectComponent implements OnInit {
    @Input() title: string = '';
    @Input() disabled: boolean = false;
    @Input() hidePreview: boolean = false;
    @Input() hidePrint: boolean = false;
    @Input() hidePdf: boolean = false;
    @Input() hideExcel: boolean = false;
    @Input() hideText: boolean = true;
    @Input() canSaveDraft: boolean = false;

    @Input() previewButtonLabel: string = 'VISTA PREVIA';
    @Input() pdfButtonLabel: string = 'EXPORTAR PDF';
    @Input() printButtonLabel: string = 'IMPRIMIR';
    @Input() excelButtonLabel: string = 'EXPORTAR EXCEL';
    @Input() textButtonLabel: string = 'EXPORTAR TXT';

    @Input() draftDataId: string;
    @Input() draftData: any;

    @Output() previewClicked = new EventEmitter<string>();
    @Output() printClicked = new EventEmitter<string>();
    @Output() pdfClicked = new EventEmitter<string>();
    @Output() excelClicked = new EventEmitter<string>();
    @Output() textClicked = new EventEmitter<string>();

    @Output() onRestoreData = new EventEmitter<any>();

    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

    public sidebarConfig: sidebarConfigItem[] = this.roleManager.isPharma() ? pharmaSidebarConfig : this.roleManager.isHospitalPharma() ? hospitalPharmaSidebarConfig : quantumSidebarConfig;

    links: sidebarConfigItem[] = [];

    constructor(
        private route: ActivatedRoute,
        private roleManager: RoleManagerService,
        private calls: CallsService,
        private logger: LoggerService
    ) {
        // Get quick access links
        this.route.url.subscribe(([url]) => {
            const { path, parameters } = url;
            const childs = this.sidebarConfig.find(item => item.link === 'reports');

            if ( childs?.items ) {
                const aux = childs.items
                .find((item: sidebarConfigItem) => 
                    item.items ? item?.items
                    .find((subItem: sidebarConfigItem) => 
                        subItem.link === `reports/${path}`
                    ) : undefined
                )

                this.links = aux?.items || [];
            }
        });
    }

    ngOnInit(): void {
        // Initialization logic
    }

    onSaveDraft(): void {
        if (this.canSaveDraft && this.draftDataId && this.draftData) {
            try {
                const jsonData = JSON.stringify(this.draftData, null, 2);
                const blob = new Blob([jsonData], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);

                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                const fileName = `draft-${this.draftDataId}-${timestamp}.json`;

                this.logger.logAction(UserAction.Click, {
                    message: 'Descarga datos reporte',
                    file: `draft-${this.draftDataId}-${timestamp}.json`
                })

                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                link.click();

                window.URL.revokeObjectURL(url);

                this.calls.openSnack('Reporte descargado correctamente');
            } catch (error) {
                console.error('Error al guardar los datos:', error);
                this.calls.openSnack('Error al guardar el reporte');
            }
        }
    }

    onRestoreDraft(): void {
        if (this.canSaveDraft && this.fileInput) {
            this.fileInput.nativeElement.click();
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0];
        const reader = new FileReader();

        reader.onload = (e: ProgressEvent<FileReader>) => {
            try {
                const content = e.target?.result as string;
                const parsedData = JSON.parse(content);
                this.onRestoreData.emit(parsedData);

                this.logger.logAction(UserAction.Click, {
                    message: 'Cargar datos reporte',
                    file: file.name
                })

                this.calls.openSnack('Reporte restaurado correctamente');
            } catch (error) {
                console.error('Error al restaurar los datos:', error);
                this.calls.openSnack('Error al restaurar el reporte. Archivo JSON inválido.');
            } finally {
                // Reset the input so the same file can be selected again
                input.value = '';
            }
        };

        reader.onerror = () => {
            this.calls.openSnack('Error al leer el archivo');
            input.value = '';
        };

        reader.readAsText(file);
    }

    onPreviewClick(): void {
        this.previewClicked.emit();
    }

    onPrintClick(): void {
        this.printClicked.emit();
    }

    onPdfClick(): void {
        this.pdfClicked.emit();
    }

    onExcelClick(): void {
        this.excelClicked.emit();
    }

    onTextClick(): void {
        this.textClicked.emit();
    }

    testPerm(item: sidebarConfigItem) {
        if (typeof(item.hasPermission) == 'function') {
            return item.hasPermission().length > 0; // Puede leer o escribir así que se debe mostrar
        }

        return item.hasPermission.length > 0; // Puede leer o escribir así que se debe mostrar
    }
}
